/**
 * 卡片创建主组件
 */

import { Upload, UploadProps, Button } from 'antd';
import cls from 'classnames';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import styles from './index.module.scss';
import { loadingIcon, uploadImageIcon } from '../../assets';
import { useAvatarSafeCheck } from '../../hooks/useSafeCheck';
import { useAppSelector } from '../../state';
import { uploadImageFile } from '../../utils/upload';
import { LocalImage } from '../Image';
import { postOwnerCreateCard } from '../../api/collection';

const { Dragger } = Upload;

interface CardCreationProps {
  cdnPrefix: string;
  source?: string;
  onCardGenerated?: (cardUrl: string) => void;
}

export const CardCreation = ({
  source = 'card_creation_page',
  cdnPrefix,
  onCardGenerated,
}: CardCreationProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { authData } = useAppSelector((state) => state.user);
  const { safeCheck } = useAvatarSafeCheck();
  
  const npcId = Number(router.query.npc_id) || 0;
  
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  
  const [generating, setGenerating] = useState(false);
  const [, setGeneratedCardUrl] = useState<string | null>(null);
  const [, setGenerateError] = useState<string | null>(null);

  // 处理图片上传
  const uploadProps: UploadProps = useMemo(
    () => ({
      name: 'file',
      accept: 'image/png,image/jpg,image/jpeg',
      showUploadList: false,
      customRequest: async (options) => {
        setUploading(true);
        setUploadError(null);
        const { file } = options;

        try {
          const { pathname } = await uploadImageFile({
            userId: authData.userId,
            file: file as File,
          });

          const imageUrl = cdnPrefix + pathname;
          
          // 校验图片是否包含敏感信息
          const pass = await safeCheck(imageUrl);
          if (pass.length === 0 || pass.includes(false)) {
            throw new Error('图片包含敏感信息，请重新上传');
          }
          
          setUploadedImageUrl(imageUrl);
          
          // 重置生成状态
          setGeneratedCardUrl(null);
          setGenerateError(null);
        } catch (error: any) {
          console.error('上传图片失败', error);
          setUploadError(error.message ||'图片上传失败，请重新上传');
        } finally {
          setUploading(false);
        }
      },
    }),
    [authData.userId, cdnPrefix, safeCheck]
  );

  // 生成卡牌
  const handleGenerateCard = useCallback(async () => {
    if (!uploadedImageUrl) return;
    
    setGenerating(true);
    setGenerateError(null);
    
    try {
      await postOwnerCreateCard({
        npc_id: npcId,
        user_id: authData.userId,
        preview_card: {
          img_url: uploadedImageUrl,
        },
      });

      // 卡牌内容就是上传的图片，不用重新调用接口
      // const cardId = result.owner_card_pool_id;
      // const { card_img_url } = await postGetOwnerCreateCardDetail({
      //   owner_card_pool_id: cardId,
      //   user_id: authData.userId,
      //   scene: Scene.RedoEditCardScene,
      // });
    
      setGeneratedCardUrl(uploadedImageUrl);
      
      if (onCardGenerated) {
        onCardGenerated(uploadedImageUrl);
      }
      toast.success('星念生成成功');
    } catch (error) {
      toast.error('星念生成失败，请重试');
    } finally {
      setGenerating(false);
    }
  }, [uploadedImageUrl, onCardGenerated, t]);

  // 重新上传
  const handleReupload = useCallback(() => {
    setUploadedImageUrl(null);
    setGeneratedCardUrl(null);
    setUploadError(null);
    setGenerateError(null);
  }, []);

  // 上传区域内容
  const uploadAreaContent = useMemo(() => {
    if (uploading) {
      return (
        <div className={styles.loading}>
          <LocalImage className={styles.loadingIcon} src={loadingIcon} />
          <p>{'上传中...'}</p>
        </div>
      );
    }
    if (uploadError) {
      return (
        <div className={styles.error}>
          <LocalImage src={uploadImageIcon} />
          <p className={styles.errorText}>{uploadError}</p>
          <div className={styles.retryBtn}>{'重试'}</div>
        </div>
      );
    }
    return (
      <>
        <LocalImage src={uploadImageIcon} className={styles.uploadIcon} />
        <p className={styles.title}>{'上传图片'}</p>
        <p className={styles.desc}>
          {'支持上传png、jpg、jpeg格式图片'}
        </p>
      </>
    );
  }, [uploading, uploadError, t]);

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        {/* 左侧：上传区域 */}
        <div className={styles.leftPanel}>
          <h3 className={styles.sectionTitle}>{'上传图片'}</h3>

          
          {!uploadedImageUrl ? (
            <Dragger {...uploadProps} className={styles.dragger}>
              <div className={styles.uploadArea}>
                {uploadAreaContent}
              </div>
            </Dragger>
          ) : (
            <div className={styles.previewArea}>
              <img 
                src={uploadedImageUrl} 
                alt="上传的图片" 
                className={styles.previewImage} 
              />
              <div className={styles.previewActions}>
                <Button 
                  type="text" 
                  onClick={handleReupload}
                  className={styles.reuploadBtn}
                  disabled={false}
                >
                  {'重新上传'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 底部操作按钮 */}
      <div className={styles.footer}>
        <Button
          type="primary"
          size="large"
          disabled={!uploadedImageUrl || generating}
          loading={generating}
          onClick={handleGenerateCard}
          className={cls(styles.generateBtn, {
            [styles.active]: uploadedImageUrl && !generating,
          })}
        >
          {generating ? ('生成中...') : ('生成星念')}
        </Button>
      </div>
    </div>
  );
};

export default CardCreation;
